import { CommonTooltips, KeyValuePair } from "@mcleod/common";
import { Component, DataSource, Label, Layout, Panel, Tab, TableCell } from "@mcleod/components";
import { ReflectiveDialogs } from "@mcleod/components/src/base/ReflectiveDialogs";
import { Api, Collection, DateUtil, HorizontalAlignment, isRunningInIDE } from "@mcleod/core";
import { AbstractUIDesigner } from "../AbstractUIDesigner";
import { ComponentIdValidationResult, ComponentIdValidator, IdValidationResult } from "../ComponentIdValidator";
import { DesignerTabDescriptor } from "../DesignerTabDescriptor";
import { DesignerDataSourcePanel } from "./DesignerDataSourcePanel";

export class DesignerTab extends Tab {
    designer: AbstractUIDesigner;
    path: string;
    private _baseVersion: boolean;
    firstSaveOfCustomLayout: boolean;
    customLayoutId: string;
    descr: string;
    createDateTime: Date;
    updateDateTime: Date;
    lastSelectedDataSource: DataSource;
    _modified: boolean;
    dataSources: Collection<DataSource> = {};
    designerPanel: Layout;
    dataSourcePanel: DesignerDataSourcePanel;
    private layoutRefences: string[] = [];
    private hasIdValidationErrors: boolean = false;
    private validationPreventedSave: boolean = false;
    private idValidator: ComponentIdValidator;


    constructor(designer: AbstractUIDesigner, path?: string, props?) {
        super();
        this._shouldAddDesignerContainerProperties = false;
        this.setProps({ fillHeight: true, fillRow: true, minHeight: 80, closeable: true, ...props })
        this.designer = designer;
        this.path = path;
        this.designerPanel = new Layout({
            fillHeight: true,
            fillRow: true,
            _designer: designer,
            scrollY: true,
            allowDecoratorOverride: true,
            applyFieldLevelPermissions: false,
            applyFieldLevelLicensing: false
        });
        this.dataSourcePanel = new DesignerDataSourcePanel(this);
        this.add(this.designerPanel);
        this.add(this.dataSourcePanel);
        this.setLayoutReferences();
        this.idValidator = new ComponentIdValidator(this);
    }

    get baseVersion(): boolean {
        return this._baseVersion;
    }

    set baseVersion(value: boolean) {
        this._baseVersion = value;
        this.imageName = value === false ? "formPencil" : null;
    }

    get isCustom(): boolean {
        return this.baseVersion === false;
    }

    get modified(): boolean {
        return this._modified;
    }

    set modified(value: boolean) {
        if (this._modified === value)
            return;
        this._modified = value;
        if (value)
            this.caption = this.caption + " *";
        else if (this.caption.endsWith(" *"))
            this.caption = this.caption.substring(0, this.caption.length - 2);
    }

    override createHeading() {
        super.createHeading();
        this.heading.id = "designerTabHeading";
        this.heading["_allowTooltipInDesigner"] = true;
    }

    dataSourceIdUpdated(dataSource: DataSource, oldId: string, newId: string): void {
        delete this.dataSources[oldId];
        this.dataSources[newId] = dataSource;
        const children = this.dataSourcePanel.getDesignerDataSourceRecursiveChildren();
        for (let j = 0; j < children.length; j++) {
            const currentChild = children[j];
            if (currentChild instanceof Label && currentChild.caption === oldId)
                currentChild.caption = newId;
        }
    }

    addErrorLabel(message: string) {
        this.designerPanel.add(new Label({ caption: message, fillRow: true, align: HorizontalAlignment.CENTER, marginTop: 32 }));
    }

    updateFromServerResponse(serverResponse: any) {
        this.baseVersion = serverResponse.base_version;
        this.customLayoutId = serverResponse.id;
        this.descr = serverResponse.descr;
        this.createDateTime = DateUtil.parseDateTime(serverResponse.create_datetime);
        this.updateDateTime = DateUtil.parseDateTime(serverResponse.update_datetime);
        this.heading.tooltipCallback = (baseTooltip, originatingEvent) => {
            return this.heading.showTooltip(this.createTooltipPanel(), null, { themeKey: "quickInfo" });
        };
        this.validationPreventedSave = false;
        this.hasIdValidationErrors = false;
        this.idValidator.updateUI();
    }

    getDescriptor(): DesignerTabDescriptor {
        return new DesignerTabDescriptor(this.path, this.baseVersion, this.customLayoutId);
    }

    createTooltipPanel(): Panel {
        let headerLabel: Label;
        const keyValuePairs: KeyValuePair[] = [];
        if (this.isCustom !== true)
            headerLabel = new Label({ caption: "This is the base version of this layout." });
        else {
            keyValuePairs.push(new KeyValuePair("Description", this.descr));
            keyValuePairs.push(new KeyValuePair("Create Date/Time", this.createDateTime != null ? DateUtil.formatDateTime(this.createDateTime) : "--"));
            keyValuePairs.push(new KeyValuePair("Update Date/Time", this.updateDateTime != null ? DateUtil.formatDateTime(this.updateDateTime) : "--"));
        }
        return CommonTooltips.createKeyValueTooltip(keyValuePairs, headerLabel, this.createLayoutReferencesPanel());
    }

    // panel contains a list of layouts this layout is nested in
    createLayoutReferencesPanel(addOnClick: boolean = true): Panel {
        let panel = null;
        if (this.layoutRefences.length > 0) {
            panel = new Panel({ margin: 0, padding: 0 });
            panel.add(new Label({ caption: "Changes to this layout will affect:", paddingTop: 12 }));
            this.layoutRefences.forEach(path => {
                const labelRef = new Label({ caption: path });
                if (addOnClick)
                    labelRef.setProps({ color: "primary", onClick: () => this._designer.openTab(path, false, true) });
                panel.add(labelRef);
            })
        }
        return panel;
    }

    private setLayoutReferences() {
        if (!isRunningInIDE() && this.path != null) {
            Api.search("layout/nested-layout-ref", { path: this.path }).then(response => {
                // If there is only a single reference, we don't have to display it
                // because they navigated here from that parent layout
                if (response?.data?.length > 1)
                    response?.data?.forEach(layout => this.layoutRefences.push(layout.path));
            });
        }
    }

    async validateSave(): Promise<boolean> {
        const result = this.validateComponentIdsIfNeeded(true);
        if (result.hasErrors()) {
            const panel = new Panel();
            panel.add(new Label({ caption: "Please resolve the following errors and try again." }));
            result.getUniqueErrors().forEach(error => panel.add(new Label({ caption: `\t\u2022 ${error}` })));
            await ReflectiveDialogs.showDialog(panel, { title: "Unable to Save Layout" });
            this.savePrevented(result, result.errors[0].component);
            return false;
        } else if (result.hasWarnings()) {
            const msg = `This layout contains ${result.warnings.length} auto-generated component ids. Would you like to review these warnings or continue saving?`;
            const saveLayout = !await ReflectiveDialogs.showYesNo(msg, "Warnings Found", {
                xVisible: false,
                yesButtonCaption: "Review Warnings",
                noButtonProps: { caption: "Continue Save", imageName: "disk" }
            });
            if (!saveLayout)
                this.savePrevented(result, result.warnings[0].component);
            return saveLayout;
        } else {
            return true;
        }
    }

    savePrevented(result: IdValidationResult, scrollToComponent: Component) {
        this.validationPreventedSave = true;
        this.designer.selectComponent(scrollToComponent);
        this.idValidator.updateUI(result, true);
        if (scrollToComponent instanceof TableCell)
            scrollToComponent = scrollToComponent.row.table;
        scrollToComponent.scrollIntoView();
    }

    async validateComponentId(component: Component): Promise<boolean> {
        let idValidationResult = this.validateComponentIdsIfNeeded();
        if (idValidationResult == null)
            idValidationResult = this.idValidator.validateComponentId(component.id);
        this.hasIdValidationErrors = idValidationResult.hasErrors();

        this.idValidator.updateUI(idValidationResult, this.validationPreventedSave);
        const componentResult: ComponentIdValidationResult = idValidationResult.errors?.find(error => error.component == component);
        const valid = componentResult?.error == null;
        if (!valid)
            await ReflectiveDialogs.showMessage(componentResult.error);
        return Promise.resolve(valid);
    }

    validateComponentIdsIfNeeded(force?: boolean): IdValidationResult {
        if (this.hasIdValidationErrors || this.validationPreventedSave || force === true) {
            const result = this.idValidator.validateActiveTab();
            this.hasIdValidationErrors = result.hasErrors();
            return result;
        }
    }
}
